// Report-param grammar for the menu-test block, ported verbatim from the coffee tree:
// menu_block / sections_block / products_block (mnmv|mnmc, mnsv|mnsc, mnpv|mnpc),
// block.coffee (mndv), ChartBaseView (cm, z), the ItemsListView base (s) and the
// SubItemsListView base (cb).
//
// The block owns eleven params, addressed by prefix; the coffee host expands each prefix with
// the question key on the way in and on the way out (sh precedent).

import { getDefaultChartMode } from '@aytm/app/stats/page/question-views/mn/lib/chart/chartModes'
import {
  DEFAULT_MENU_VIEW,
  DEFAULT_PRODUCT_VIEW,
  DEFAULT_SECTION_VIEW,
  MENU_VIEW_OPTIONS,
  PRODUCT_PERCENT_CAPABLE_VIEWS,
  PRODUCT_VIEW_OPTIONS,
  SECTION_VIEW_OPTIONS
} from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'
import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { encodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'
import { PERCENT_CHART_MODES } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

export { DEFAULT_SORT_ORDER }

// Unlike sh's grammar, the items-list base stores `undefined` on an unrecognised token instead
// of falling back to the default — kept deliberately (D5).
export const resolveSortOrder = (section, defaultSortOrder = DEFAULT_SORT_ORDER) =>
  decodeSortParam(section, { defaultSortOrder, passUnknown: true })

// `split('|')[0]` validated against the option list, else the default (menu/sections/products
// blocks' importReportParams).
const resolveViewMode = (param, options, fallback) => {
  if (null == param) return fallback

  const first = String(param).split('|')[0]
  return first && options.some(option => option.value === first) ? first : fallback
}

// '[]' is the explicit zero cohort; a stale param whose options were all removed falls back to
// all-selected; absent means all selected (block.coffee importDeliveryParams). The fall-back
// flags `needsUpdate`: the request is answered against the stored param, so left in place it
// narrows the cohort to nothing while the control reads "All delivery options".
const resolveDeliveryOptions = (param, deliveryOptions = []) => {
  if ('[]' === param) return { selected: [], needsUpdate: false }
  if (null == param) return { selected: deliveryOptions.slice(), needsUpdate: false }

  const recognized = String(param)
    .split('|')
    .filter(opt => deliveryOptions.includes(opt))
  return recognized.length
    ? { selected: recognized, needsUpdate: false }
    : { selected: deliveryOptions.slice(), needsUpdate: true }
}

// SubItemsListView position addressing, uid-first. Exported because the card list has to key its
// rows by the same rule the filter below applies to them: keyed by anything else, a toggle arrives
// under a name the filter does not recognise and is dropped without a sound.
export const sectionPosition = section => String(section?.uid?.replace('.', '_').replace('@', '_') || section?.position)

// `cb{key}`: split on '.', uniq, filter to live section positions, lexicographic sort.
// `needsUpdate` is the self-heal signal: the base rewrites the stored param whenever the healed
// join differs from it — only when the stored param had content (trim gate), exactly as
// SubItemsListView.importReportParams does.
const resolveUnCollapsedSections = (param, sections = []) => {
  const stored = param || ''
  if (!(0 < stored.trim().length)) return { unCollapsedSections: [], needsUpdate: false }

  const unCollapsed = stored
    .split('.')
    .filter((d, i, all) => all.indexOf(d) === i)
    .filter(d => sections.find(section => d === sectionPosition(section)))
    .sort()

  return { unCollapsedSections: unCollapsed, needsUpdate: unCollapsed.join('.') !== stored }
}

// params → block state. `needsUpdate` rides along for the cb and mndv self-heals (not state
// itself — the hook writes the healed section out when it is set).
export const resolveReportParams = (params = {}, context = {}) => {
  const { deliveryOptions = [], sections = [], defaultSortOrder = DEFAULT_SORT_ORDER } = context

  const { unCollapsedSections, needsUpdate: cardsNeedUpdate } = resolveUnCollapsedSections(params.cb, sections)
  const delivery = resolveDeliveryOptions(params.mndv, deliveryOptions)

  return {
    menuViewMode: resolveViewMode(params.mnmv, MENU_VIEW_OPTIONS, DEFAULT_MENU_VIEW),
    menuCollapsed: params.mnmc ?? false,
    sectionsViewMode: resolveViewMode(params.mnsv, SECTION_VIEW_OPTIONS, DEFAULT_SECTION_VIEW),
    sectionsCollapsed: params.mnsc ?? false,
    productViewMode: resolveViewMode(params.mnpv, PRODUCT_VIEW_OPTIONS, DEFAULT_PRODUCT_VIEW),
    productsCollapsed: params.mnpc ?? false,
    selectedDeliveryOptions: delivery.selected,
    chartMode: params.cm || null,
    zoomLevel: params.z,
    sortOrder: resolveSortOrder(params.s, defaultSortOrder),
    unCollapsedSections,
    needsUpdate: cardsNeedUpdate || delivery.needsUpdate
  }
}

// all selected → default state, param cleared; empty → literal '[]' zero-cohort marker; else a
// schema-ordered join for stable content-addressing on the stat server (D7)
const calcDeliveryParam = (selected = [], deliveryOptions = []) => {
  if (selected.length === deliveryOptions.length) return null
  if (0 === selected.length) return '[]'

  return deliveryOptions.filter(opt => selected.includes(opt)).join('|')
}

// The mode the products chart opens on once the question and report-wide defaults are applied --
// what the exporters draw by, as opposed to the stored `cm`, which is null until the researcher
// picks one. The products chart is the one the block's axis bridge already answers for.
export const calcResolvedChartMode = (state = {}, context = {}) =>
  getDefaultChartMode({
    chartMode: state.chartMode || context.modelChartMode,
    globalChartMode: context.globalChartMode,
    viewMode: state.productViewMode,
    percentCapableViews: PRODUCT_PERCENT_CAPABLE_VIEWS,
    itemsCount: (context.answerVariants || []).filter(variant => !variant.na).length
  })

// The whole param set the block owns, keyed by prefix. Every entry is written on every change:
// a value that did not move resolves to what is already stored, and a null clears the param.
export const calcReportParamsSection = (state = {}, context = {}) => {
  const {
    deliveryOptions = [],
    format = {},
    modelChartMode,
    globalZoomLevel,
    defaultSortOrder = DEFAULT_SORT_ORDER
  } = context
  const {
    menuViewMode,
    menuCollapsed,
    sectionsViewMode,
    sectionsCollapsed,
    productViewMode,
    productsCollapsed,
    selectedDeliveryOptions = [],
    chartMode,
    zoomLevel,
    sortOrder,
    unCollapsedSections = []
  } = state

  // the mode the question itself declares; a selection matching it needs no param
  const declaredChartMode = format?.chartMode || modelChartMode || 'off'

  return {
    mnmv: DEFAULT_MENU_VIEW === menuViewMode ? null : `${menuViewMode}`,
    mnmc: menuCollapsed || null,
    mnsv: DEFAULT_SECTION_VIEW === sectionsViewMode ? null : `${sectionsViewMode}`,
    mnsc: sectionsCollapsed || null,
    mnpv: DEFAULT_PRODUCT_VIEW === productViewMode ? null : `${productViewMode}`,
    mnpc: productsCollapsed || null,
    mndv: calcDeliveryParam(selectedDeliveryOptions, deliveryOptions),
    cm: chartMode === declaredChartMode ? null : chartMode,
    z: encodeZoomParam(zoomLevel, globalZoomLevel),
    // default first (a default that happens to be 'vasc' still nulls, like the coffee switch);
    // an undefined sortOrder (unrecognised import, D5) passes through as undefined
    s: encodeSortParam(sortOrder, { defaultSortOrder }),
    cb: unCollapsedSections.length ? unCollapsedSections.join('.') : null
  }
}

// SubItemsListView.updateUnCollapsed port — a card toggle: add/remove the position, then the
// same uniq/filter/sort pipeline the import runs.
export const updateUnCollapsedSections = (unCollapsed = [], sections = [], position, collapsed) => {
  const next = collapsed ? unCollapsed.filter(d => d !== String(position)) : [...unCollapsed, String(position)]

  return next
    .filter((d, i, all) => all.indexOf(d) === i)
    .filter(d => sections.find(section => d === sectionPosition(section)))
    .sort()
}

// "Expand all" — every card open (toggleAll expands each card in turn; the end state is the
// full sorted position list)
export const calcAllUnCollapsedSections = (sections = []) => sections.map(sectionPosition).sort()

export { PERCENT_CHART_MODES }

// D6 cross-param resets, applied by the hook when a sections/products view mode actually
// changes: the zoom is always dropped; the chart mode only when the view being entered has no
// percent axis while the one being left had, with a percent mode saved.
export const calcViewModeResets = ({ prevViewMode, nextViewMode, percentCapableViews = [], chartMode } = {}) => ({
  zoomLevel: null,
  ...(percentCapableViews.includes(prevViewMode) &&
  !percentCapableViews.includes(nextViewMode) &&
  PERCENT_CHART_MODES.includes(chartMode)
    ? { chartMode: null }
    : {})
})
